import React, { useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import { useListContext, useRecordContext } from 'react-admin';

import { ICase } from '../../../utilities/schemas/case';
import getCaseDescriptionText from '../utils/description';
import useLoadingDots from '../../../hooks/useLoadingDots';

export const CASE_DESCRIPTION_MAX_WIDTH = '300px';

const CaseDescription = () => {
  const record = useRecordContext<ICase>();
  const { refetch } = useListContext<ICase>();
  const {
    text,
    hasError,
    isPending,
    shouldShowSummary,
  } = getCaseDescriptionText(record?.aiSummary);

  useEffect(() => {
    if (isPending) {
      const interval = setInterval(() => {
        refetch();
      }, 1000);
      return () => clearInterval(interval);
    }

    return undefined;
  }, [isPending, refetch]);

  const { loadingDots, loadingDotsStyle } = useLoadingDots(isPending);
  if (!record) return null;

  if (hasError || !shouldShowSummary) return (<Typography>{text}</Typography>);

  if (isPending) {
    return (
      <Typography sx={loadingDotsStyle}>
        {text}
        {loadingDots}
      </Typography>
    );
  }

  return (
    <Tooltip
      title={text}
      placement="top-start"
    >
      <Box
        component="span"
        tabIndex={0}
        sx={{
          cursor: 'default',
          display: 'inline-flex',
          width: '100%',
          '&:focus-visible': {
            borderRadius: 1,
            outline: '2px solid',
            outlineColor: 'primary.main',
          },
        }}
      >
        <Typography
          component="span"
          sx={{
            borderBottom: '1px dotted',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {text}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default CaseDescription;
